import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemText,
	Checkbox,
} from "@mui/material";
import { useTranslation } from "next-i18next";
import { useState, forwardRef, useImperativeHandle } from "react";
import type { ProjectPosition } from "../types/ProjectTypes";

export type DialogAddPositionsToTeamMemberArgs = {
	availablePositions: ProjectPosition[];
	onConfirm: (selectedPositionIds: string[]) => void;
};

export type DialogAddPositionsToTeamMemberHandle = {
	show: (args: DialogAddPositionsToTeamMemberArgs) => void;
};

export const DialogAddPositionsToTeamMember = forwardRef<DialogAddPositionsToTeamMemberHandle>(
	(props, ref) => {
		const { t } = useTranslation();
		const [open, setOpen] = useState(false);
		const [availablePositions, setAvailablePositions] = useState<ProjectPosition[]>([]);
		const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
		const [onConfirm, setOnConfirm] = useState<((ids: string[]) => void) | null>(null);

		useImperativeHandle(ref, () => ({
			show: (args: DialogAddPositionsToTeamMemberArgs) => {
				setAvailablePositions(args.availablePositions);
				setSelectedIds(new Set());
				setOnConfirm(() => args.onConfirm);
				setOpen(true);
			},
		}));

		const handleToggle = (positionId: string) => {
			setSelectedIds((prev) => {
				const updated = new Set(prev);
				if (updated.has(positionId)) {
					updated.delete(positionId);
				} else {
					updated.add(positionId);
				}
				return updated;
			});
		};

		const handleConfirm = () => {
			if (onConfirm) {
				onConfirm(Array.from(selectedIds));
			}
			setOpen(false);
		};

		return (
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>{t("add-positions")}</DialogTitle>
				<DialogContent>
					<List>
						{availablePositions.map((position) => (
							<ListItem
								key={position.id}
								component="button"
								onClick={() => handleToggle(position.id)}
								sx={{ cursor: "pointer", textAlign: "left", width: "100%" }}
							>
								<Checkbox checked={selectedIds.has(position.id)} tabIndex={-1} disableRipple />
								<ListItemText
									primary={position.service?.name}
									secondary={`${t("hours")}: ${position.hours?.toFixed(2) || "-"}`}
								/>
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>{t("cancel")}</Button>
					<Button onClick={handleConfirm} variant="contained" disabled={selectedIds.size === 0}>
						{t("add")}
					</Button>
				</DialogActions>
			</Dialog>
		);
	},
);

DialogAddPositionsToTeamMember.displayName = "DialogAddPositionsToTeamMember";
